import React, { useState } from 'react';
import { Container, Typography, Grid, Card, CardContent, Box, Button, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const posts = [
  {
    title: '5 Signs Your Laptop Battery Needs Replacing',
    excerpt: 'Is your laptop dying faster than it used to? Here are the telltale signs that your battery is failing and what you can do about it.',
    category: 'Device Tips',
    date: 'April 10, 2026',
    readTime: '3 min read',
    image: null,
  },
  {
    title: 'How to Protect Your Business from Ransomware in 2026',
    excerpt: 'Ransomware attacks are surging. Learn the top 7 steps every small business should take right now to avoid becoming a victim.',
    category: 'Cybersecurity',
    date: 'April 5, 2026',
    readTime: '5 min read',
    image: null,
  },
  {
    title: 'Microsoft 365 vs Google Workspace: Which Is Right for Your Business?',
    excerpt: 'Choosing a productivity suite is a big decision. We break down the pros, cons, and pricing to help you pick the right platform.',
    category: 'Cloud & Productivity',
    date: 'March 28, 2026',
    readTime: '6 min read',
    image: null,
  },
  {
    title: 'Why Your Home WiFi Is Slow (And How to Fix It)',
    excerpt: 'Buffering, dead zones, and dropped connections — we explain the most common causes and how to get the most out of your router.',
    category: 'Networking',
    date: 'March 20, 2026',
    readTime: '4 min read',
    image: null,
  },
  {
    title: 'What Is Zero Trust Security and Why Does It Matter?',
    excerpt: '"Never trust, always verify." We explain the Zero Trust framework in plain English and why it\'s become the gold standard for network security.',
    category: 'Cybersecurity',
    date: 'March 12, 2026',
    readTime: '5 min read',
    image: null,
  },
  {
    title: 'Top 10 Windows 11 Settings You Should Change Today',
    excerpt: 'Windows 11 has some useful tweaks hidden in the settings menu. Here are the ones that will make the biggest difference in speed and privacy.',
    category: 'Device Tips',
    date: 'March 5, 2026',
    readTime: '4 min read',
    image: null,
  },
  {
    title: 'How to Recover Data from a Dead Hard Drive',
    excerpt: 'Heard a clicking noise from your hard drive? Don\'t panic. We walk you through the steps — and explain when to call a professional.',
    category: 'Data Recovery',
    date: 'February 25, 2026',
    readTime: '4 min read',
    image: null,
  },
  {
    title: 'Understanding Microsoft Intune: A Small Business Guide',
    excerpt: 'Managing company devices doesn\'t have to be complicated. Intune lets you secure and configure all your devices from one dashboard.',
    category: 'Cloud & Productivity',
    date: 'February 15, 2026',
    readTime: '6 min read',
    image: null,
  },
  {
    title: 'How to Speed Up a Slow PC Without Buying New Hardware',
    excerpt: 'Before you spend money on a new computer, try these proven software and hardware tweaks to breathe new life into your old machine.',
    category: 'Device Tips',
    date: 'February 8, 2026',
    readTime: '5 min read',
    image: null,
  },
];

const categories = ['All', 'Device Tips', 'Cybersecurity', 'Cloud & Productivity', 'Networking', 'Data Recovery'];

const categoryColors = {
  'Device Tips':          { bg: 'rgba(255,107,107,0.12)', color: '#ff6b6b' },
  'Cybersecurity':        { bg: 'rgba(255,70,130,0.12)',  color: '#ff4680' },
  'Cloud & Productivity': { bg: 'rgba(100,180,255,0.12)', color: '#64b4ff' },
  'Networking':           { bg: 'rgba(255,217,61,0.12)',  color: '#ffd93d' },
  'Data Recovery':        { bg: 'rgba(0,200,150,0.12)',   color: '#00c896' },
};

function Blog() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? posts
    : posts.filter((p) => p.category === activeCategory);

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{
        position: 'absolute', top: '6%', right: '4%',
        width: 400, height: 400,
        background: 'radial-gradient(circle, rgba(255,107,107,0.09) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />

      <Box sx={{ position: 'relative', zIndex: 1, py: 10 }}>
        <Container maxWidth="lg">

          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 3, display: 'block', mb: 2 }}>
              Tech Tips & Insights
            </Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.2rem' }, fontWeight: 800, color: '#fff', mb: 2 }}>
              The AkdmeTech{' '}
              <Box component="span" className="gradient-text">Blog</Box>
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', lineHeight: 1.8 }}>
              Expert advice, how-to guides, and IT tips for homes and small businesses.
            </Typography>
          </Box>

          {/* Category filter */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 6, justifyContent: 'center' }}>
            {categories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                onClick={() => setActiveCategory(cat)}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  background: activeCategory === cat
                    ? 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)'
                    : 'rgba(255,107,107,0.08)',
                  color: activeCategory === cat ? '#2d3436' : '#fff',
                  border: activeCategory === cat ? 'none' : '1px solid rgba(255,107,107,0.2)',
                  cursor: 'pointer',
                }}
              />
            ))}
          </Box>

          {/* Posts grid */}
          <Grid container spacing={3}>
            {filtered.map((post, i) => {
              const catStyle = categoryColors[post.category] || { bg: 'rgba(255,107,107,0.1)', color: '#ff6b6b' };
              return (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Card sx={{
                    height: '100%',
                    background: 'rgba(255,107,107,0.05)',
                    border: '1px solid rgba(255,107,107,0.1)',
                    borderRadius: '16px',
                    backdropFilter: 'blur(10px)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex', flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      border: '1px solid rgba(255,107,107,0.28)',
                      boxShadow: '0 12px 30px rgba(255,107,107,0.12)',
                    },
                  }}>
                    {/* Color bar */}
                    <Box sx={{
                      height: 4,
                      background: `linear-gradient(90deg, ${catStyle.color}, transparent)`,
                      borderRadius: '16px 16px 0 0',
                    }} />
                    <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Chip
                          label={post.category}
                          size="small"
                          sx={{
                            background: catStyle.bg,
                            color: catStyle.color,
                            fontWeight: 600,
                            fontSize: '0.72rem',
                          }}
                        />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', mb: 1.5, lineHeight: 1.4, flexGrow: 1 }}>
                        {post.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                        {post.excerpt}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">{post.readTime}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">{post.date}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* CTA */}
          <Box sx={{ textAlign: 'center', mt: 10 }}>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 1.5 }}>
              Need Help With Your Device or Network?
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
              Our team is ready to assist — book a service or contact us today.
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/book-service')}
              sx={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)',
                color: '#2d3436', fontWeight: 700, px: 5,
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(255,107,107,0.3)' },
                transition: 'all 0.3s ease',
              }}
            >
              Book a Service
            </Button>
          </Box>

        </Container>
      </Box>
    </Box>
  );
}

export default Blog;
